import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CalendarSync,
  CheckCircle2,
  Presentation,
  RefreshCw,
  Unplug,
  XCircle,
} from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { api } from "@/lib/api";
import { useToast } from "@/components/ui/Toast";
import { cn, formatDateLong } from "@/lib/utils";
import { SETTINGS_DEFAULTS } from "@shared/schema";
import { parseOwnerEmails } from "@shared/matching";

interface GoogleStatus {
  connected: boolean;
  userEmail?: string | null;
  calendarId?: string;
  filterPattern?: string;
  lastSyncAt?: string | null;
  lastSyncError?: string | null;
  lastSyncCount?: number | null;
}

export function SettingsPage() {
  const [status, setStatus] = useState<GoogleStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [params, setParams] = useSearchParams();
  const toast = useToast();

  const refresh = useCallback(async () => {
    try {
      const s = await api.googleStatus();
      setStatus(s);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erreur");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const flag = params.get("google");
    if (flag === "connected") {
      toast.success("Google Calendar connecté");
      params.delete("google");
      setParams(params, { replace: true });
    } else if (flag === "error") {
      toast.error(params.get("msg") ?? "Erreur connexion Google");
      params.delete("google");
      params.delete("msg");
      setParams(params, { replace: true });
    }
  }, [params, setParams, toast]);

  async function sync() {
    setSyncing(true);
    try {
      const r = await api.googleSync();
      toast.success(
        `Sync OK · ${r.fetched} évén., ${r.prospectsCreated} nouveau(x) prospect(s), ${r.prospectsLinked} lié(s)`,
      );
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erreur sync");
    } finally {
      setSyncing(false);
    }
  }

  async function disconnect() {
    if (!confirm("Déconnecter Google Calendar ?")) return;
    try {
      await api.googleDisconnect();
      toast.success("Déconnecté");
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erreur");
    }
  }

  return (
    <>
      <PageHeader
        title="Paramètres"
        subtitle="Intégrations et configuration du CRM"
      />

      <section className="surface p-6 max-w-3xl">
        <div className="flex items-start gap-4 mb-5">
          <div className="h-10 w-10 rounded-lg bg-[var(--color-navy-600)] grid place-items-center flex-shrink-0">
            <CalendarSync size={20} className="text-white" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-[var(--color-navy-700)]">
              Google Calendar
            </h2>
            <p className="text-sm text-[var(--color-muted-foreground)] mt-0.5">
              Synchronise tes RDV Calendly (qui arrivent dans ton agenda
              Google) pour créer ou enrichir les prospects automatiquement.
            </p>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Chargement...
          </p>
        ) : !status?.connected ? (
          <div className="space-y-4">
            <div className="rounded-[var(--radius-md)] bg-[var(--color-navy-50)] border border-[var(--color-navy-100)] p-4 text-sm">
              <p className="text-[var(--color-navy-700)] font-medium mb-1">
                Pas encore connecté
              </p>
              <p className="text-[var(--color-muted-foreground)] leading-relaxed">
                Tu seras redirigé vers Google pour autoriser l'accès en lecture
                à ton calendrier. Seuls les événements dont le titre contient
                le filtre seront importés (par défaut : "Power Coaching").
              </p>
            </div>
            <a
              href="/api/google/connect"
              className="btn btn-primary inline-flex"
            >
              <CalendarSync size={16} /> Connecter Google Calendar
            </a>
          </div>
        ) : (
          <>
            <div className="rounded-[var(--radius-md)] bg-[color-mix(in_srgb,#16a34a_8%,white)] border border-[color-mix(in_srgb,#16a34a_20%,white)] p-4 mb-5">
              <div className="flex items-start gap-3">
                <CheckCircle2
                  size={18}
                  className="text-[#0f6b30] flex-shrink-0 mt-0.5"
                />
                <div className="flex-1 text-sm">
                  <p className="font-medium text-[#0f6b30]">
                    Connecté · {status.userEmail ?? "compte Google"}
                  </p>
                  <p className="text-[#0f6b30]/80 mt-1">
                    {status.lastSyncAt ? (
                      <>Dernière sync : {formatDateLong(status.lastSyncAt)}</>
                    ) : (
                      <>Aucune sync exécutée pour l'instant.</>
                    )}
                    {typeof status.lastSyncCount === "number" &&
                      status.lastSyncCount > 0 && (
                        <> · {status.lastSyncCount} évén. récupérés</>
                      )}
                  </p>
                </div>
              </div>
            </div>

            {status.lastSyncError && (
              <div className="rounded-[var(--radius-md)] border border-[var(--color-coral-200)] bg-[var(--color-coral-50)] p-3 mb-5 text-sm text-[var(--color-coral-700)] flex items-start gap-2">
                <XCircle size={16} className="flex-shrink-0 mt-0.5" />
                <span>{status.lastSyncError}</span>
              </div>
            )}

            <FilterForm
              initial={status.filterPattern ?? ""}
              onSaved={refresh}
            />

            <div className="mt-6 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={sync}
                disabled={syncing}
                className="btn btn-accent"
              >
                <RefreshCw
                  size={14}
                  className={cn(syncing && "animate-spin")}
                />
                {syncing ? "Sync en cours..." : "Sync maintenant"}
              </button>
              <button
                type="button"
                onClick={disconnect}
                className="btn btn-danger"
              >
                <Unplug size={14} /> Déconnecter
              </button>
            </div>

            <p className="text-xs text-[var(--color-muted-foreground)] mt-5">
              Sync automatique en arrière-plan toutes les 30 minutes via un
              cron Cloudflare.
            </p>
          </>
        )}
      </section>

      <WebinarSettings />
    </>
  );
}

const DEFAULT_OWNERS = SETTINGS_DEFAULTS.owner_emails.split(",").join(" et ");

function WebinarSettings() {
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const [loaded, setLoaded] = useState<{ owner: string; url: string } | null>(null);
  const [owner, setOwner] = useState("");
  const [calendly, setCalendly] = useState("");
  const [saving, setSaving] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);

  useEffect(() => {
    // Chargement unique : `toast` est lu par référence pour ne pas relancer la requête.
    let active = true;
    api
      .settings()
      .then(({ settings }) => {
        if (!active) return;
        const o = settings.owner_emails ?? "";
        const u = settings.calendly_booking_url ?? "";
        setLoaded({ owner: o, url: u });
        setOwner(o);
        setCalendly(u);
      })
      .catch((e) => toastRef.current.error(e instanceof Error ? e.message : "Erreur"));
    return () => {
      active = false;
    };
  }, []);

  const dirty =
    loaded !== null && (owner.trim() !== loaded.owner || calendly.trim() !== loaded.url);

  async function save(e: FormEvent) {
    e.preventDefault();
    setFieldError(null);
    if (parseOwnerEmails(owner).length === 0) {
      setFieldError(
        `Garde au moins une adresse à exclure (par défaut : ${DEFAULT_OWNERS}), sinon tes propres e-mails seraient importés comme prospects.`,
      );
      return;
    }
    if (calendly.trim() !== loaded?.url) {
      let ok = false;
      try {
        ok = new URL(calendly.trim()).protocol === "https:";
      } catch {
        ok = false;
      }
      if (!ok) {
        setFieldError("Le lien Calendly doit être une adresse complète commençant par https://");
        return;
      }
    }
    setSaving(true);
    try {
      const { settings } = await api.updateSettings({
        ...(owner.trim() !== loaded?.owner ? { owner_emails: owner.trim() } : {}),
        ...(calendly.trim() !== loaded?.url ? { calendly_booking_url: calendly.trim() } : {}),
      });
      const o = settings.owner_emails ?? owner.trim();
      const u = settings.calendly_booking_url ?? calendly.trim();
      setLoaded({ owner: o, url: u });
      setOwner(o);
      setCalendly(u);
      toast.success("Réglages enregistrés");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erreur");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="surface p-6 max-w-3xl mt-6">
      <div className="flex items-start gap-4 mb-5">
        <div className="h-10 w-10 rounded-lg bg-[var(--color-navy-600)] grid place-items-center flex-shrink-0">
          <Presentation size={20} className="text-white" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-[var(--color-navy-700)]">
            Webinaires et messages
          </h2>
          <p className="text-sm text-[var(--color-muted-foreground)] mt-0.5">
            Ce qui sert à l'import des webinaires et aux messages WhatsApp prêts à
            envoyer.
          </p>
        </div>
      </div>

      {loaded === null ? (
        <p className="text-sm text-[var(--color-muted-foreground)]">Chargement...</p>
      ) : (
        <form onSubmit={save} className="space-y-5">
          <label className="block">
            <span className="text-xs font-medium text-[var(--color-muted-foreground)] uppercase tracking-wide">
              E-mails à exclure de l'import
            </span>
            <input
              className="input-base mt-1.5"
              value={owner}
              onChange={(e) => {
                setOwner(e.target.value);
                setFieldError(null);
              }}
              placeholder="@christophedang.com, moi@exemple.com"
            />
            <p className="text-xs text-[var(--color-muted-foreground)] mt-2 leading-relaxed">
              Tes propres adresses, séparées par une virgule. Une adresse commençant
              par <code>@</code> couvre tout le domaine. Par défaut : {DEFAULT_OWNERS}.
            </p>
          </label>

          <label className="block">
            <span className="text-xs font-medium text-[var(--color-muted-foreground)] uppercase tracking-wide">
              Lien Calendly des messages
            </span>
            <input
              className="input-base mt-1.5"
              value={calendly}
              onChange={(e) => {
                setCalendly(e.target.value);
                setFieldError(null);
              }}
              placeholder="https://calendly.com/…"
              inputMode="url"
            />
            <p className="text-xs text-[var(--color-muted-foreground)] mt-2 leading-relaxed">
              Le lien de prise de RDV glissé dans les messages WhatsApp.
            </p>
          </label>

          {fieldError && (
            <div
              role="alert"
              className="rounded-[var(--radius-md)] border border-[var(--color-coral-200)] bg-[var(--color-coral-50)] p-3 text-sm text-[var(--color-coral-700)]"
            >
              {fieldError}
            </div>
          )}

          <button type="submit" className="btn btn-outline" disabled={saving || !dirty}>
            {saving ? "..." : "Sauvegarder"}
          </button>
        </form>
      )}
    </section>
  );
}

function FilterForm({
  initial,
  onSaved,
}: {
  initial: string;
  onSaved: () => void;
}) {
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!value.trim()) return;
    setSaving(true);
    try {
      await api.googleUpdateConfig({ filterPattern: value.trim() });
      toast.success("Filtre mis à jour");
      onSaved();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erreur");
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={save} className="space-y-2">
      <label className="block">
        <span className="text-xs font-medium text-[var(--color-muted-foreground)] uppercase tracking-wide">
          Filtre sur le titre de l'événement
        </span>
        <div className="mt-1.5 flex gap-2">
          <input
            className="input-base flex-1"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Power Coaching"
          />
          <button
            type="submit"
            className="btn btn-outline"
            disabled={saving || value === initial}
          >
            {saving ? "..." : "Sauvegarder"}
          </button>
        </div>
        <p className="text-xs text-[var(--color-muted-foreground)] mt-2 leading-relaxed">
          Mots-clés séparés par <code>|</code> ou virgule. Exemple :
          <code className="ml-1">Power Coaching | plan d'action</code> ne
          gardera que les événements dont le titre contient l'un des deux.
        </p>
      </label>
    </form>
  );
}
